import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Add, Clear, Close } from '@mui/icons-material';
import {
  colors,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';

import { STOItemStatusEnum } from 'commons/enums';
import S from 'commons/styles/assign-sto-item-page.styles';
import { STOItem } from 'commons/types';
import {
  actionAddAssignedSTOReceiveItem,
  actionRemoveAssignedSTOReceiveItem,
  actionRemoveAssignedSTOReceiveItemByBoxPallet,
  actionSetAssignedSTOReceiveItem,
  actionSetAssignedSTOReceiveItemByBoxPallet,
  actionStartSTOReceiveItem,
  getSTOReceiveAssignedItems,
} from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';

import { RemoveSTOReceiveItemDialog } from './components';

export const AssignSTOReceiveItemPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { assignedSTOItems } = useSelector(
    (state: RootReducerInterface) => state.stoReceiveItem,
  );
  const [openRemoveModal, setOpenRemoveModal] = useState<boolean>(false);
  const [removedSTOItem, setRemovedSTOItem] = useState<STOItem | null>(null);
  const [pallet, setPallet] = useState<string>('');
  const [box, setBox] = useState<string>('');

  useEffect(() => {
    dispatch(getSTOReceiveAssignedItems());
  }, [dispatch]);

  const assignSTOItem = (): void => {
    if (assignedSTOItems.length === 0) {
      dispatch(actionSetAssignedSTOReceiveItem());
    } else {
      dispatch(actionAddAssignedSTOReceiveItem());
    }
  };

  const assignSTOItemByBoxPallet = (): void => {
    dispatch(actionSetAssignedSTOReceiveItemByBoxPallet({ box, pallet })).then(
      () => {
        setBox('');
        setPallet('');
      },
    );
  };

  const startReceiving = async () => {
    dispatch(actionStartSTOReceiveItem({ navigate, items: assignedSTOItems }));
  };

  const onRemoveModalOpen = (item: STOItem): void => {
    setOpenRemoveModal(true);
    setRemovedSTOItem(item);
  };
  const onRemoveModalClose = (): void => setOpenRemoveModal(false);
  const removeSTO = (item: STOItem): void => {
    dispatch(
      actionRemoveAssignedSTOReceiveItem({
        stoNumber: item.sto_number,
        skuCode: item.sku_code,
      }),
    );
    setOpenRemoveModal(false);
  };
  const removeSTOByBoxPallet = (): void => {
    // assume all box pallet is same in one batch
    const payload = {
      box: assignedSTOItems[0].box_number || '',
      pallet: assignedSTOItems[0].pallet || '',
    };
    dispatch(actionRemoveAssignedSTOReceiveItemByBoxPallet(payload));
    setOpenRemoveModal(false);
  };

  const validateAssignment = (): boolean => {
    return assignedSTOItems.length > 0;
  };

  const showCancelAssignmentButton = () =>
    assignedSTOItems.every((item) => item.status === STOItemStatusEnum.CHECKED);

  const renderBoxPalletForm = () => (
    <Grid container>
      <Grid item xs={4}>
        <p>Box</p>
      </Grid>
      <Grid item xs={6}>
        <TextField
          variant="outlined"
          placeholder="-"
          value={box}
          onChange={(event) => setBox(event.target.value)}
          id="field-assign-box"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setBox('')}>
                  <Clear style={{ color: S.colors.PRIMARY_GRAY }} />
                </IconButton>
              </InputAdornment>
            ),
            sx: { paddingRight: 0 },
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <p>Pallet</p>
      </Grid>
      <Grid item xs={6}>
        <TextField
          variant="outlined"
          placeholder="-"
          value={pallet}
          onChange={(event) => setPallet(event.target.value)}
          id="field-assign-box"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setPallet('')}>
                  <Clear style={{ color: S.colors.PRIMARY_GRAY }} />
                </IconButton>
              </InputAdornment>
            ),
            sx: { paddingRight: 0 },
          }}
        />
      </Grid>
      <Grid item xs={12} style={{ textAlign: 'center' }}>
        <S.PrimaryButton
          disabled={!box || !pallet}
          onClick={assignSTOItemByBoxPallet}
          data-testid="BtnAssignStartReceiving"
        >
          START RECEIVING
        </S.PrimaryButton>
      </Grid>
    </Grid>
  );

  const renderSkuList = () => (
    <>
      {showCancelAssignmentButton() && (
        <S.CancelAssignmentButton
          onClick={() => setOpenRemoveModal(true)}
          data-testid="BtnCancelTOReceiveItemAssignment"
        >
          Cancel Assignment
        </S.CancelAssignmentButton>
      )}
      {assignedSTOItems.map((item, idx) => (
        <S.GridWrapper container>
          <Grid item xs={12}>
            <S.SkuNumberGridWrapper container>
              <S.SkuInfoGrid item xs={5} lg={1.25}>
                <p data-testid={`TextAssignSTOReceiveItem-${idx + 1}`}>
                  SKU {item.sku_code}
                </p>
              </S.SkuInfoGrid>
              <S.SkuInfoGrid className="sto-number" item xs={7}>
                <S.STONumberInfoGrid
                  data-testid="TextAssignReceiveItemSTONumber"
                  style={{ textTransform: 'uppercase' }}
                >
                  {item.sto_number}
                </S.STONumberInfoGrid>
              </S.SkuInfoGrid>
              <S.SkuInfoGrid
                item
                xs={6}
                data-testid={`TextAssignQTYReceive-${idx + 1}`}
              >
                <S.AdditionalInfoGrid>
                  QTY Receive:{' '}
                  {item.pick_actual.reduce(
                    (total, _item) => _item.quantity + total,
                    0,
                  )}
                </S.AdditionalInfoGrid>
              </S.SkuInfoGrid>
              <S.SkuInfoGrid
                item
                xs={6}
                style={{ textAlign: 'right' }}
                data-testid={`TextAssignPalletPosition-${idx + 1}`}
              >
                <S.AdditionalInfoGrid>
                  <p>Box Number: {item.box_number}</p>
                  <p>Pallet Position: {item.pallet}</p>
                </S.AdditionalInfoGrid>
              </S.SkuInfoGrid>
            </S.SkuNumberGridWrapper>
          </Grid>
        </S.GridWrapper>
      ))}
      {/* Footer Section */}
      <S.FooterWrapper>
        <S.PrimaryButton
          disabled={!validateAssignment()}
          onClick={startReceiving}
          data-testid="BtnAssignStartReceiving"
        >
          START RECEIVING
        </S.PrimaryButton>
      </S.FooterWrapper>
    </>
  );

  return (
    <div>
      {/* Content Section */}
      <S.ContentWrapper>
        <S.SubtitleWrapper container>
          <S.Subtitle item xs={12} data-testid="TextAssignSTOReceiveItem">
            Assign SKU
          </S.Subtitle>
        </S.SubtitleWrapper>

        {!validateAssignment() ? renderBoxPalletForm() : renderSkuList()}

        {/* <S.AddSTOItemButtonWrapper>
          <S.SecondaryButton
            onClick={assignSTOItem}
            data-testid="BtnAssignSTOReceiveItem"
          >
            <Add />{' '}
            {assignedSTOItems.length > 0 ? 'ASSIGN ANOTHER SKU' : 'ASSIGN SKU'}
          </S.SecondaryButton>
        </S.AddSTOItemButtonWrapper> */}
        <S.BlankWhiteSpace />
      </S.ContentWrapper>

      {/* Modal */}
      {openRemoveModal && (
        <RemoveSTOReceiveItemDialog
          onClose={onRemoveModalClose}
          onConfirm={removeSTOByBoxPallet}
          openModal={openRemoveModal}
        />
      )}
    </div>
  );
};
